import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  ArrowLeft,
  User,
  Building2,
  Edit,
  Save,
  X,
  Plus,
  ExternalLink,
  FileText,
  MessageSquare,
  EyeIcon,
  EyeOffIcon,
  Play,
  Square,
  Timer,
  Trash2,
  Copy,
  Paperclip,
} from "lucide-react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { toast } from "react-hot-toast";
import { clientService } from "../../services/clientService";
import * as helperService from "../../services/helperService";
import * as constantValues from "../../constants/constants";
import DeletableAsyncOption from "../deletableAsyncOption";

const createEmptyQuickBooks = () => ({
  type: "",
  username: "",
  password: "",
});

const createEmptyCredential = () => ({
  id: null,
  entity_name: "",
  entity_link: "",
  username: "",
  password: "",
});

const createEmptyDependant = () => ({
  id: null,
  name: "",
  date_birth: "",
  ssn: "",
  relationship: "",
});

const createEmptyOwner = () => ({
  id: null,
  name: "",
  ssn: "",
  date_birth: "",
  percentage: "",
  address: "",
});

const NotePreviewPanel = ({ preview, onClose }) => {
  if (!preview?.url) return null;

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800 truncate">
          Preview: {preview.label}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-700 rounded transition"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {preview.type.startsWith("image/") && (
        <img
          src={preview.url}
          alt="Attachment preview"
          className="max-h-96 w-auto object-contain mx-auto"
        />
      )}

      {preview.type === "application/pdf" && (
        <iframe title="PDF preview" src={preview.url} className="w-full h-96" />
      )}

      {(preview.type.startsWith("text/") ||
        preview.type === "application/json") && (
        <pre className="p-3 text-sm overflow-auto max-h-96 bg-gray-50">
          {preview.text ?? "—"}
        </pre>
      )}

      {!preview.type.startsWith("image/") &&
        preview.type !== "application/pdf" &&
        !preview.type.startsWith("text/") &&
        preview.type !== "application/json" && (
          <div className="p-3 text-sm text-gray-600">
            Preview not supported ({preview.type}).{" "}
            <button
              type="button"
              onClick={() => {
                const link = document.createElement("a");
                link.href = preview.url;
                link.download = preview.label || "attachment";
                link.click();
              }}
              className="text-primary-600 hover:underline"
            >
              Download file
            </button>
          </div>
        )}
    </div>
  );
};

const ClientDetail = ({
  user,
  userPermission,
  onClientUpdated,
  onClientDeleted,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("data");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [salesRepDefaults, setSalesRepDefaults] = useState([]);
  const [clientManagerDefaults, setClientManagerDefaults] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingContent, setEditingContent] = useState("");
  const [newNoteFile, setNewNoteFile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activityType, setActivityType] = useState("");
  const [description, setDescription] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const tickRef = useRef(null);
  const noteFileInputRef = useRef(null);
  const [activeNotePreview, setActiveNotePreview] = useState(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);
  const [selectedMember, setSelectedMember] = useState("");
  const [deletingSalesRepId, setDeletingSalesRepId] = useState(null);
  const [deletingClientManagerId, setDeletingClientManagerId] = useState(null);
  const [isQuickbooksEditing, setIsQuickbooksEditing] = useState(false);
  const [quickbooksDraft, setQuickbooksDraft] = useState(
    createEmptyQuickBooks(),
  );
  const [editingCredentialId, setEditingCredentialId] = useState(null);
  const [credentialDraft, setCredentialDraft] = useState(
    createEmptyCredential(),
  );
  const [visibleCredentialPasswords, setVisibleCredentialPasswords] = useState(
    new Set(),
  );
  const [editingDependantId, setEditingDependantId] = useState(null);
  const [dependantDraft, setDependantDraft] = useState(createEmptyDependant());
  const [editingOwnerId, setEditingOwnerId] = useState(null);
  const [ownerDraft, setOwnerDraft] = useState(createEmptyOwner());
  const currentUser = useMemo(
    () => selectedMember || user?.name || "",
    [selectedMember, user?.name],
  );
  const clientIdentifier = client?.id;
  const isPerson = client?.type === "person";
  const clientDisplayName = client?.data.name;
  const isInactiveClient = client?.data?.status?.trim() === "Inactive";
  const rawNotes = client?.notes;
  const rawTracking = client?.tracking;
  const selectedStatesFiling = Array.isArray(
    editData.data?.state_filing ?? editData.state_filing,
  )
    ? (editData.data?.state_filing ?? editData.state_filing)
    : [];
  const selectedFilingStates = Array.isArray(
    editData.data?.filing_state ?? editData.filing_state,
  )
    ? (editData.data?.filing_state ?? editData.filing_state)
    : [];
  const stateOptions = constantValues.usStateOptions.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const loadClient = useCallback(async () => {
    try {
      setLoading(true);
      const clientData = await clientService.getClient(id);
      setClient(clientData);
      setEditData(clientData);
    } catch (error) {
      toast.error("Failed to load client");
      console.error("Load client error:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  useEffect(() => {
    setNotes(helperService.normalizeNotes(rawNotes));
  }, [rawNotes]);

  const closeNotePreview = () => {
    setActiveNotePreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setPreviewLoadingKey(null);
  };

  const setPreviewFromBlob = async ({ key, label, blob, contentType }) => {
    const normalizedType =
      contentType || blob.type || "application/octet-stream";
    let text = null;
    if (
      normalizedType.startsWith("text/") ||
      normalizedType === "application/json"
    ) {
      try {
        text = await blob.text();
      } catch {
        text = null;
      }
    }
    const url = URL.createObjectURL(blob);
    setActiveNotePreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { key, label, url, type: normalizedType, text };
    });
  };

  const handlePreviewNote = async (note, key, label) => {
    if (!note.asset_url) return;
    setPreviewLoadingKey(key);
    try {
      const { blob, contentType } = await clientService.getNoteFile(
        clientIdentifier,
        note.asset_url,
      );
      await setPreviewFromBlob({ key, label, blob, contentType });
    } catch (e) {
      toast.error("Failed to load preview");
      console.error(e);
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const handlePreviewSelectedFile = async (file, key, label) => {
    setPreviewLoadingKey(key);
    try {
      await setPreviewFromBlob({
        key,
        label,
        blob: file,
        contentType: file.type || "application/octet-stream",
      });
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  useEffect(() => {
    return () => {
      if (activeNotePreview?.url) URL.revokeObjectURL(activeNotePreview.url);
    };
  }, [activeNotePreview]);

  useEffect(() => {
    setSessions(Array.isArray(rawTracking) ? rawTracking : []);
  }, [rawTracking]);

  useEffect(() => {
    setIsQuickbooksEditing(false);
    setQuickbooksDraft(createEmptyQuickBooks());
    setEditingCredentialId(null);
    setCredentialDraft(createEmptyCredential());
    setEditingDependantId(null);
    setDependantDraft(createEmptyDependant());
    setEditingOwnerId(null);
    setOwnerDraft(createEmptyOwner());
  }, [clientIdentifier]);

  const handleSave = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    try {
      const updatedClient = await clientService.updateClient(clientIdentifier, {
        type: editData.type,
        data: editData.data,
        documents: editData.documents,
      });

      const prepared = helperService.prepareClientData(updatedClient);

      setClient(prepared);
      setEditData(prepared);
      setIsEditing(false);
      onClientUpdated(prepared);
      toast.success("Client updated successfully");
      return true;
    } catch (error) {
      toast.error("Failed to update client");
      console.error("Update error:", error);
      return false;
    }
  };

  const handleDelete = async () => {
    if (userPermission !== "masterAccess") {
      toast.error("You do not have permission to delete clients");
      return;
    }
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    const clientName = client.data.name;

    if (
      window.confirm(
        `Are you sure you want to delete ${clientName}? This action cannot be undone.`,
      ) &&
      window.confirm(
        `Do you really want to delete ${clientName}? Click “Ok” only if you are absolutely sure.`,
      )
    ) {
      try {
        await clientService.deleteClient(clientIdentifier);
        onClientDeleted(clientIdentifier);
        toast.success("Client deleted successfully");
        navigate("/");
      } catch (error) {
        toast.error("Failed to delete client");
        console.error("Delete error:", error);
      }
    }
  };

  const toggleCredentialPasswordVisibility = (credentialId) => {
    setVisibleCredentialPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(credentialId)) {
        next.delete(credentialId);
      } else {
        next.add(credentialId);
      }
      return next;
    });
  };

  const handleCopyValue = async (value, label) => {
    if (!value) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      toast.error(`Unable to copy ${label}`);
      console.error("Copy clipboard error:", error);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const title = newNoteTitle.trim();
    const content = newNoteContent.trim();
    if (!title && !content) return;

    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }

    const optimistic = {
      id: `tmp_${Date.now()}`,
      title,
      content,
      author: currentUser,
      who_updated_last: currentUser,
      asset_url: null,
      _optimistic: true,
    };

    const previousFile = newNoteFile;

    setNotes((prev) => [optimistic, ...prev]);
    setNewNoteTitle("");
    setNewNoteContent("");
    setNewNoteFile(null);
    if (noteFileInputRef.current) {
      noteFileInputRef.current.value = "";
    }

    try {
      let asset_path = undefined;
      if (previousFile) {
        const uploaded = await clientService.uploadNoteAttachment(
          clientIdentifier,
          {
            title: title || "Note",
            author: currentUser,
            file: previousFile,
          },
        );
        // prefer a public URL if your FastAPI returns one (e.g. /files/<name>)
        asset_path = uploaded?.path || null;

        // update optimistic card to show the attachment link ASAP
        if (asset_path) {
          setNotes((prev) =>
            prev.map((n) =>
              n.id === optimistic.id ? { ...n, asset_url: asset_path } : n,
            ),
          );
        }
      }

      const saved = await clientService.addNote(clientIdentifier, {
        title: title || "Note",
        content,
        author: currentUser,
        who_updated_last: currentUser,
        asset_url: asset_path,
      });
      setNotes((prev) =>
        prev.map((n) =>
          n.id === optimistic.id
            ? { ...optimistic, ...saved, _optimistic: false }
            : n,
        ),
      );
      toast.success("Note added successfully");
    } catch (err) {
      setNotes((prev) => prev.filter((n) => n.id !== optimistic.id));
      setNewNoteTitle(title);
      setNewNoteContent(content);
      setNewNoteFile(previousFile || null);
      if (noteFileInputRef.current) noteFileInputRef.current.value = "";
      toast.error("Failed to add note");
      console.error(err);
    }
  };

  const handleNoteFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setNewNoteFile(file);
  };

  const clearNewNoteFile = () => {
    setNewNoteFile(null);
    if (noteFileInputRef.current) noteFileInputRef.current.value = "";
    closeNotePreview();
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingTitle(note.title || "");
    setEditingContent(note.content || "");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingTitle("");
    setEditingContent("");
  };

  const saveEditNote = async (id) => {
    const title = editingTitle.trim();
    const content = editingContent.trim();
    const now = new Date().toISOString();

    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }

    const prev = notes;
    setNotes((cur) =>
      cur.map((n) =>
        n.id === id
          ? {
              ...n,
              title,
              content,
              who_updated_last: currentUser,
              updated_at: now,
            }
          : n,
      ),
    );
    cancelEditNote();

    try {
      await clientService.updateNote(clientIdentifier, id, {
        title,
        content,
        who_updated_last: currentUser,
      });
      toast.success("Note updated");
    } catch (err) {
      setNotes(prev);
      toast.error("Failed to update note");
      console.error(err);
    }
  };

  const handleDeleteNote = async (event, id) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    const noteTitle =
      notes.find((n) => n.id === id)?.title?.trim() || "this note";
    const confirmed = window.confirm(
      `Delete ${noteTitle}? This cannot be undone.`,
    );
    if (!confirmed) return;
    const prev = notes;
    setNotes((cur) => cur.filter((n) => n.id !== id));

    try {
      await clientService.deleteNote(clientIdentifier, id);
      toast.success("Note deleted");
    } catch (err) {
      setNotes(prev);
      toast.error("Failed to delete note");
      console.error("Delete note error:", err);
    }
  };

  const refreshCredentials = useCallback(async () => {
    if (!clientIdentifier) return [];
    try {
      const creds = await clientService.getCredentials(clientIdentifier);
      setClient((prev) => (prev ? { ...prev, credentials: creds } : prev));
      setEditData((prev) => (prev ? { ...prev, credentials: creds } : prev));
      return creds;
    } catch (error) {
      console.error("Refresh credentials error:", error);
      toast.error("Failed to refresh credentials");
      return [];
    }
  }, [clientIdentifier]);

  const refreshDependants = useCallback(async () => {
    if (!clientIdentifier) return [];
    try {
      const deps = await clientService.listDependants(clientIdentifier);
      setClient((prev) => (prev ? { ...prev, dependants: deps } : prev));
      return deps;
    } catch (error) {
      console.error("Refresh dependents error:", error);
      toast.error("Failed to refresh dependents");
      return [];
    }
  }, [clientIdentifier]);

  const refreshOwnership = useCallback(async () => {
    if (!clientIdentifier) return [];
    try {
      const owners = await clientService.listOwnership(clientIdentifier);
      setClient((prev) => (prev ? { ...prev, ownership: owners } : prev));
      return owners;
    } catch (error) {
      console.error("Refresh ownership error:", error);
      toast.error("Failed to refresh ownership");
      return [];
    }
  }, [clientIdentifier]);

  const startQuickbooksEdit = () => {
    setQuickbooksDraft({
      type: client?.quickbooks?.type || "",
      username: client?.quickbooks?.username || "",
      password: client?.quickbooks?.password || "",
    });
    setIsQuickbooksEditing(true);
  };

  const cancelQuickbooksEdit = () => {
    setIsQuickbooksEditing(false);
    setQuickbooksDraft(createEmptyQuickBooks());
  };

  const handleSaveQuickbooks = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (!quickbooksDraft.type.trim()) {
      toast.error("Type is required");
      return;
    }
    try {
      const payload = {
        type: quickbooksDraft.type.trim(),
        username: quickbooksDraft.username.trim(),
        password: quickbooksDraft.password,
      };
      const updated = await clientService.upsertQuickBooks(
        clientIdentifier,
        payload,
      );
      setClient((prev) => (prev ? { ...prev, quickbooks: updated } : prev));
      setEditData((prev) => (prev ? { ...prev, quickbooks: updated } : prev));
      toast.success("QuickBooks updated");
      cancelQuickbooksEdit();
    } catch (error) {
      toast.error("Failed to update QuickBooks");
      console.error("QuickBooks save error:", error);
    }
  };

  const handleDeleteQuickbooks = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (!client?.quickbooks) {
      toast.error("No QuickBooks data to delete");
      return;
    }
    if (
      !window.confirm(
        "Delete QuickBooks credentials for this client? This cannot be undone.",
      )
    ) {
      return;
    }
    try {
      await clientService.deleteQuickBooks(clientIdentifier);
      setClient((prev) => (prev ? { ...prev, quickbooks: null } : prev));
      setEditData((prev) => (prev ? { ...prev, quickbooks: null } : prev));
      cancelQuickbooksEdit();
      toast.success("QuickBooks deleted");
    } catch (error) {
      toast.error("Failed to delete QuickBooks");
      console.error("QuickBooks delete error:", error);
    }
  };

  const startEditCredential = (credential = null) => {
    if (credential) {
      setEditingCredentialId(credential.id);
      setCredentialDraft({
        id: credential.id,
        entity_name: credential.entity_name || "",
        entity_link: credential.entity_link || "",
        username: credential.username || "",
        password: credential.password || "",
      });
    } else {
      setEditingCredentialId("new");
      setCredentialDraft(createEmptyCredential());
    }
  };

  const cancelCredentialEdit = () => {
    setEditingCredentialId(null);
    setCredentialDraft(createEmptyCredential());
  };

  const handleSaveCredential = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (!credentialDraft.entity_name.trim()) {
      toast.error("Entity name is required");
      return;
    }
    const payload = {
      entity_name: credentialDraft.entity_name.trim(),
      entity_link: credentialDraft.entity_link.trim(),
      username: credentialDraft.username.trim(),
      password: credentialDraft.password,
    };
    try {
      if (!credentialDraft.id) {
        await clientService.createCredential(clientIdentifier, payload);
        toast.success("Credential added");
      } else {
        await clientService.updateCredential(
          clientIdentifier,
          credentialDraft.id,
          payload,
        );
        toast.success("Credential updated");
      }
      await refreshCredentials();
      cancelCredentialEdit();
    } catch (error) {
      toast.error("Failed to save credential");
      console.error("Credential save error:", error);
    }
  };

  const handleDeleteCredential = async (credential) => {
    if (userPermission !== "masterAccess") {
      toast.error("You do not have permission to delete clients");
      return;
    }
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (
      !window.confirm(
        `Delete credential "${credential.entity_name || "Unnamed"}"?`,
      )
    ) {
      return;
    }
    try {
      await clientService.deleteCredential(clientIdentifier, credential.id);
      if (editingCredentialId === credential.id) {
        cancelCredentialEdit();
      }
      await refreshCredentials();
      toast.success("Credential deleted");
    } catch (error) {
      toast.error("Failed to delete credential");
      console.error("Credential delete error:", error);
    }
  };

  const startEditDependant = (dependant = null) => {
    if (dependant) {
      setEditingDependantId(dependant.id);
      setDependantDraft({
        id: dependant.id,
        name: dependant.name || "",
        date_birth: dependant.date_birth || "",
        ssn: dependant.ssn || "",
        relationship: dependant.relationship || "",
      });
    } else {
      setEditingDependantId("new");
      setDependantDraft(createEmptyDependant());
    }
  };

  const cancelDependantEdit = () => {
    setEditingDependantId(null);
    setDependantDraft(createEmptyDependant());
  };

  const handleSaveDependant = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    const { name, date_birth, ssn, relationship } = dependantDraft;
    if (!name.trim() || !date_birth || !ssn.trim() || !relationship.trim()) {
      toast.error("All dependent fields are required");
      return;
    }
    const payload = {
      name: name.trim(),
      date_birth,
      ssn: ssn.trim(),
      relationship: relationship.trim(),
    };
    try {
      if (!dependantDraft.id) {
        await clientService.addDependant(clientIdentifier, payload);
        toast.success("Dependent added");
      } else {
        await clientService.updateDependant(
          clientIdentifier,
          dependantDraft.id,
          payload,
        );
        toast.success("Dependent updated");
      }
      await refreshDependants();
      cancelDependantEdit();
    } catch (error) {
      toast.error("Failed to save dependent");
      console.error("Dependent save error:", error);
    }
  };

  const handleDeleteDependant = async (dependant) => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (!window.confirm(`Delete dependent "${dependant.name || "Unnamed"}"?`)) {
      return;
    }
    try {
      await clientService.deleteDependant(clientIdentifier, dependant.id);
      if (editingDependantId === dependant.id) {
        cancelDependantEdit();
      }
      await refreshDependants();
      toast.success("Dependent deleted");
    } catch (error) {
      toast.error("Failed to delete dependent");
      console.error("Dependent delete error:", error);
    }
  };

  const startEditOwner = (owner = null) => {
    if (owner) {
      setEditingOwnerId(owner.id);
      setOwnerDraft({
        id: owner.id,
        name: owner.name || "",
        ssn: owner.ssn || "",
        date_birth: owner.date_birth || "",
        percentage: owner.percentage ?? "",
        address: owner.address || "",
      });
    } else {
      setEditingOwnerId("new");
      setOwnerDraft(createEmptyOwner());
    }
  };

  const cancelOwnerEdit = () => {
    setEditingOwnerId(null);
    setOwnerDraft(createEmptyOwner());
  };

  const handleSaveOwner = async () => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    const pctValue = parseFloat(ownerDraft.percentage);
    if (
      !ownerDraft.name.trim() ||
      !ownerDraft.ssn.trim() ||
      Number.isNaN(pctValue)
    ) {
      toast.error("Name, SSN, and percentage are required");
      return;
    }
    if (pctValue < 0 || pctValue > 100) {
      toast.error("Percentage must be between 0 and 100");
      return;
    }
    const baseList = client?.ownership || [];
    const totalExcludingCurrent = baseList.reduce((sum, owner) => {
      if (editingOwnerId && owner.id === editingOwnerId) return sum;
      return sum + (parseFloat(owner.percentage) || 0);
    }, 0);
    if (totalExcludingCurrent + pctValue > 100.000001) {
      toast.error("Total ownership cannot exceed 100%");
      return;
    }
    const payload = {
      name: ownerDraft.name.trim(),
      ssn: ownerDraft.ssn.trim(),
      date_birth: ownerDraft.date_birth || null,
      percentage: pctValue,
      address: ownerDraft.address.trim(),
    };
    try {
      if (!ownerDraft.id) {
        await clientService.addOwner(clientIdentifier, payload);
        toast.success("Owner added");
      } else {
        await clientService.updateOwner(
          clientIdentifier,
          ownerDraft.id,
          payload,
        );
        toast.success("Owner updated");
      }
      await refreshOwnership();
      cancelOwnerEdit();
    } catch (error) {
      toast.error("Failed to save owner");
      console.error("Owner save error:", error);
    }
  };

  const handleDeleteOwner = async (owner) => {
    if (!clientIdentifier) {
      toast.error("Missing client identifier");
      return;
    }
    if (!window.confirm(`Delete owner "${owner.name || "Unnamed"}"?`)) {
      return;
    }
    try {
      await clientService.deleteOwner(clientIdentifier, owner.id);
      if (editingOwnerId === owner.id) {
        cancelOwnerEdit();
      }
      await refreshOwnership();
      toast.success("Owner deleted");
    } catch (error) {
      toast.error("Failed to delete owner");
      console.error("Owner delete error:", error);
    }
  };

  const handleStartTimer = (e) => {
    e?.preventDefault?.();
    if (!activityType || !selectedMember) return;
    const startIso = new Date().toISOString();
    setStartedAt(startIso);
    setIsRunning(true);
  };

  const handleStopTimer = async () => {
    if (!isRunning || !startedAt) return;
    const endIso = new Date().toISOString();
    const duration = new Date(endIso) - new Date(startedAt);

    const optimistic = {
      id: `${Date.now()}`,
      activity_type: activityType,
      description: description?.trim() || "",
      started_at: startedAt,
      ended_at: endIso,
      duration_ms: duration,
      created_by: selectedMember,
      _optimistic: true,
    };

    setSessions((prev) => [optimistic, ...prev]);
    setIsRunning(false);
    setStartedAt(null);
    setElapsedMs(0);
    setDescription("");

    try {
      const saved = await clientService.addTrackingEntry(clientIdentifier, {
        activity_type: activityType,
        description: description?.trim() || "",
        started_at: startedAt,
        ended_at: endIso,
        created_by: selectedMember,
      });
      // replace optimistic with server row (has real id & created_by from server)
      setSessions((prev) =>
        prev.map((s) => (s.id === optimistic.id ? { ...saved } : s)),
      );
    } catch (error) {
      setSessions((prev) => prev.filter((s) => s.id !== optimistic.id));
      console.error("Failed to save time entry", error);
      toast.error("Failed to save time entry");
    }
  };

  const handleDeleteSession = async (id) => {
    const prev = sessions;
    setSessions((cur) => cur.filter((s) => s.id !== id));
    try {
      const res = await clientService.deleteTrackingEntry(clientIdentifier, id);
      if (!res?.ok) throw new Error("Delete failed");
      toast.success("Time entry deleted");
    } catch (e) {
      console.error("Failed to delete time entry", e);
      setSessions(prev); // revert
      toast.error("Failed to delete time entry");
    }
  };

  const handleTabChange = async (nextTabId) => {
    if (activeTab === "data" && nextTabId !== "data" && infoDirty) {
      const saveNow = window.confirm(
        "You have unsaved info. Save before leaving?",
      );
      if (saveNow) {
        const ok = await handleSave();
        if (!ok) return; // stay on tab if save failed
        setActiveTab(nextTabId);
      } else {
        setIsEditing(false); // discard edits
        setEditData(client); // reset form to saved values
        setActiveTab(nextTabId);
      }
      return;
    }
    setActiveTab(nextTabId);
  };

  useEffect(() => {
    (async () => {
      try {
        const [reps, mgrs] = await Promise.all([
          clientService.getSalesReps(""),
          clientService.getClientManagers(""),
        ]);
        setSalesRepDefaults(reps.map((r) => ({ value: r.id, label: r.name })));
        setClientManagerDefaults(
          mgrs.map((m) => ({ value: m.id, label: m.name })),
        );
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const selectedSalesRepOption = useMemo(() => {
    const id = editData?.data?.sales_rep_id;
    if (!id) return null;

    const name =
      editData?.data?.sales_rep_name ??
      salesRepDefaults.find((o) => o.value === id)?.label ??
      `#${id}`;
    return { value: id, label: name };
  }, [
    editData?.data?.sales_rep_id,
    editData?.data?.sales_rep_name,
    salesRepDefaults,
  ]);

  const selectedClientMgrOption = useMemo(() => {
    const id = editData?.data?.client_manager_id;
    if (!id) return null;
    const name =
      editData?.data?.client_manager_name ??
      clientManagerDefaults.find((o) => o.value === id)?.label ??
      `#${id}`;
    return { value: id, label: name };
  }, [
    editData?.data?.client_manager_id,
    editData?.data?.client_manager_name,
    clientManagerDefaults,
  ]);

  const salesRepOptions = useMemo(() => {
    const sel = selectedSalesRepOption;
    return sel && !salesRepDefaults.some((o) => o.value === sel.value)
      ? [sel, ...salesRepDefaults]
      : salesRepDefaults;
  }, [selectedSalesRepOption, salesRepDefaults]);

  const clientMgrOptions = useMemo(() => {
    const sel = selectedClientMgrOption;
    return sel && !clientManagerDefaults.some((o) => o.value === sel.value)
      ? [sel, ...clientManagerDefaults]
      : clientManagerDefaults;
  }, [selectedClientMgrOption, clientManagerDefaults]);

  const loadSalesRepOptions = async (inputValue) => {
    const rows = await clientService.getSalesReps(inputValue);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };
  const loadClientManagerOptions = async (inputValue) => {
    const rows = await clientService.getClientManagers(inputValue);
    return rows.map((m) => ({ value: m.id, label: m.name }));
  };

  const debounce = (fn, delay = 300) => {
    let t;
    return (...args) =>
      new Promise((resolve) => {
        clearTimeout(t);
        t = setTimeout(async () => resolve(await fn(...args)), delay);
      });
  };
  const debouncedLoadSales = useMemo(() => debounce(loadSalesRepOptions), []);
  const debouncedLoadManagers = useMemo(
    () => debounce(loadClientManagerOptions),
    [],
  );

  const handleDeleteSalesRepOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete sales rep "${option.label}"? This will unassign it from all clients or leads.`,
      )
    ) {
      return;
    }
    try {
      setDeletingSalesRepId(option.value);
      await clientService.deleteSalesRep(option.value);
      setSalesRepDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setEditData((prev) => {
        if (prev?.data?.sales_rep_id !== option.value) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            sales_rep_id: null,
            sales_rep_name: null,
          },
        };
      });
      toast.success(`Sales rep "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete sales rep error", error);
      toast.error("Could not delete Sales Rep");
    } finally {
      setDeletingSalesRepId(null);
    }
  }, []);

  const handleDeleteClientManagerOption = useCallback(async (option) => {
    if (!option?.value) return;
    if (
      !window.confirm(
        `Delete client manager "${option.label}"? This will unassign it from all clients.`,
      )
    ) {
      return;
    }
    try {
      setDeletingClientManagerId(option.value);
      await clientService.deleteClientManager(option.value);
      setClientManagerDefaults((prev) =>
        prev.filter((item) => item.value !== option.value),
      );
      setEditData((prev) => {
        if (prev?.data?.client_manager_id !== option.value) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            client_manager_id: null,
            client_manager_name: null,
          },
        };
      });
      toast.success(`Client manager "${option.label}" deleted`);
    } catch (error) {
      console.error("Delete client manager error", error);
      toast.error("Could not delete Client Manager");
    } finally {
      setDeletingClientManagerId(null);
    }
  }, []);

  useEffect(() => {
    if (!isRunning || !startedAt) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = setInterval(() => {
      setElapsedMs(new Date() - new Date(startedAt));
    }, 1000);
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isRunning, startedAt]);

  const totalMs = useMemo(
    () => sessions.reduce((acc, s) => acc + (Number(s.duration_ms) || 0), 0),
    [sessions],
  );

  const ownershipTotal = useMemo(() => {
    const list = client?.ownership || [];
    return list.reduce((sum, o) => sum + (parseFloat(o?.percentage) || 0), 0);
  }, [client?.ownership]);
  const ownershipOver = ownershipTotal > 100.000001;

  const credentialsCount = client?.credentials?.length ?? 0;
  const dependantsCount = client?.dependants?.length ?? 0;
  const ownershipCount = client?.ownership?.length ?? 0;

  const tabs = useMemo(() => {
    if (!client) return [];
    return helperService.createTabs({
      userPermission: userPermission,
      clientType: client.type,
      notesCount: notes.length,
      sessionsCount: sessions.length,
      credentialsCount,
      dependantsCount,
      ownershipCount,
    });
  }, [
    userPermission,
    client,
    credentialsCount,
    dependantsCount,
    ownershipCount,
    notes.length,
    sessions.length,
  ]);

  const infoDirty = useMemo(() => {
    if (!isEditing || !client) return false;
    return (
      JSON.stringify(editData?.data ?? {}) !==
      JSON.stringify(client?.data ?? {})
    );
  }, [isEditing, client, editData?.data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Client not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/clients")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Clients
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={`p-3 rounded-lg ${
                isInactiveClient
                  ? "bg-red-100 text-red-600"
                  : isPerson
                    ? "bg-blue-100 text-blue-600"
                    : "bg-green-100 text-green-600"
              }`}
            >
              {isPerson ? (
                <User className="h-6 w-6" />
              ) : (
                <Building2 className="h-6 w-6" />
              )}
            </div>
            <div>
              <h1
                className={`text-3xl font-bold ${
                  isInactiveClient ? "text-red-600" : "text-gray-900"
                }`}
              >
                {clientDisplayName}
              </h1>
              <p
                className={`mt-1 ${
                  isInactiveClient ? "text-red-500" : "text-gray-600"
                }`}
              >
                {isPerson ? "Personal" : "Business"} • Client ID {client.id}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center px-4 py-2 rounded-lg text-white transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap gap-x-6 gap-y-2 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Information Tab */}
          {activeTab === "data" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {isPerson ? "Personal Information" : "Business Information"}
              </h3>

              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {isPerson ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filing Status
                        </label>
                        <select
                          value={editData.data?.filing_status || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                filing_status: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select status</option>
                          {constantValues.personFilingStatusOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filing Details
                        </label>
                        <select
                          value={editData.data?.filing_details || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                filing_details: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select details</option>
                          {constantValues.filingDetailsOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State Filing
                        </label>
                        <Select
                          name="state_filing"
                          isMulti
                          options={stateOptions}
                          value={stateOptions.filter((o) =>
                            selectedStatesFiling.includes(o.value),
                          )}
                          onChange={(selected) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                state_filing: (selected || []).map(
                                  (o) => o.value,
                                ),
                              },
                            }))
                          }
                          closeMenuOnSelect={false}
                          placeholder="Select states"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={editData.data?.name || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                name: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={editData.data?.name || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                name: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Name
                        </label>
                        <input
                          type="text"
                          value={editData.data?.contact_name || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                contact_name: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          DBA
                        </label>
                        <input
                          type="text"
                          value={editData.data?.dba || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: { ...editData.data, dba: e.target.value },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filing Status
                        </label>
                        <select
                          value={editData.data?.filing_status || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                filing_status: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select status</option>
                          {constantValues.businessfilingStatusOptions.map(
                            (t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Filing State
                        </label>
                        <Select
                          name="filing_state"
                          isMulti
                          options={stateOptions}
                          value={stateOptions.filter((o) =>
                            selectedFilingStates.includes(o.value),
                          )}
                          onChange={(selected) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                filing_state: (selected || []).map(
                                  (o) => o.value,
                                ),
                              },
                            }))
                          }
                          closeMenuOnSelect={false}
                          placeholder="Select states"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          EIN
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editData.data?.ein || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                ein: e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 9),
                              },
                            })
                          }
                          maxLength={9}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Common fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editData.data?.email || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: { ...editData.data, email: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Email
                    </label>
                    <input
                      type="email"
                      value={editData.data?.secondary_email || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: {
                            ...editData.data,
                            secondary_email: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={editData.data?.phone || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: { ...editData.data, phone: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Phone Numbers
                    </label>
                    <input
                      type="tel"
                      value={editData.data?.additional_phones || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: {
                            ...editData.data,
                            additional_phones: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {isPerson ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SSN
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editData.data?.ssn || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                ssn: e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 9),
                              },
                            })
                          }
                          maxLength={9}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={editData.data?.date_birth || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                date_birth: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Spouse Name
                        </label>
                        <input
                          type="text"
                          value={editData.data?.spouse_name || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                spouse_name: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Spouse SSN
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editData.data?.spouse_ssn || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                spouse_ssn: e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 9),
                              },
                            })
                          }
                          maxLength={9}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Spouse Date of Birth
                        </label>
                        <input
                          type="date"
                          value={editData.data?.spouse_date_birth || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                spouse_date_birth: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </>
                  ) : (
                    <></>
                  )}

                  {/* Common fields */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={editData.data?.address || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: { ...editData.data, address: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City, State, Zip
                    </label>
                    <input
                      type="text"
                      value={editData.data?.city || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: { ...editData.data, city: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      value={editData.data?.country || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          data: { ...editData.data, country: e.target.value },
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {isPerson ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Foreign
                        </label>
                        <select
                          value={editData.data?.international || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                international: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select option</option>
                          {constantValues.internationalOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Incorporation Date
                        </label>
                        <input
                          type="date"
                          value={editData.data?.incorporation_date || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                incorporation_date: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Incorporation State
                        </label>
                        <input
                          type="text"
                          value={editData.data?.incorporation_state || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                incorporation_state: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Industry
                        </label>
                        <input
                          type="text"
                          value={editData.data?.industry || ""}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              data: {
                                ...editData.data,
                                industry: e.target.value,
                              },
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Foreing
                        </label>
                        <select
                          value={editData.data?.international || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                international: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select option</option>
                          {constantValues.internationalOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Accounting Method
                        </label>
                        <select
                          value={editData.data?.accounting_method || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                accounting_method: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select method</option>
                          {constantValues.accountingMethodOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Common fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Method of Contact
                    </label>
                    <select
                      value={editData.data?.contact_method || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            contact_method: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select method</option>
                      {constantValues.contactMethodOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      value={editData.data?.language || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            language: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select language</option>
                      {constantValues.languageOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sales Representative
                    </label>
                    <AsyncCreatableSelect
                      isClearable
                      cacheOptions
                      defaultOptions={salesRepOptions}
                      loadOptions={debouncedLoadSales}
                      value={selectedSalesRepOption}
                      components={{ Option: DeletableAsyncOption }}
                      userPermission={userPermission}
                      onDeleteOption={handleDeleteSalesRepOption}
                      deletingOptionId={deletingSalesRepId}
                      onChange={(opt) =>
                        setEditData((prev) => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            sales_rep_id: opt?.value ?? null,
                            sales_rep_name: opt?.label ?? null,
                          },
                        }))
                      }
                      onCreateOption={async (name) => {
                        try {
                          const created =
                            await clientService.createSalesRep(name);
                          const opt = {
                            value: created.id,
                            label: created.name,
                          };
                          setSalesRepDefaults((prev) => [...prev, opt]);
                          setEditData((prev) => ({
                            ...prev,
                            data: {
                              ...prev.data,
                              sales_rep_id: created.id,
                              sales_rep_name: created.name,
                            },
                          }));
                        } catch (e) {
                          console.error(e);
                          toast.error("Could not create Sales Rep");
                        }
                      }}
                      placeholder="Select or create a sales rep"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Manager
                    </label>
                    <AsyncCreatableSelect
                      isClearable
                      cacheOptions
                      defaultOptions={clientMgrOptions}
                      loadOptions={debouncedLoadManagers}
                      value={selectedClientMgrOption}
                      components={{ Option: DeletableAsyncOption }}
                      userPermission={userPermission}
                      onDeleteOption={handleDeleteClientManagerOption}
                      deletingOptionId={deletingClientManagerId}
                      onChange={(opt) =>
                        setEditData((prev) => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            client_manager_id: opt?.value ?? null,
                            client_manager_name: opt?.label ?? null,
                          },
                        }))
                      }
                      onCreateOption={async (name) => {
                        try {
                          const created =
                            await clientService.createClientManager(name);
                          const opt = {
                            value: created.id,
                            label: created.name,
                          };
                          setClientManagerDefaults((prev) => [...prev, opt]);
                          setEditData((prev) => ({
                            ...prev,
                            data: {
                              ...prev.data,
                              client_manager_id: created.id,
                              client_manager_name: created.name,
                            },
                          }));
                        } catch (e) {
                          console.error(e);
                          toast.error("Could not create Client Manager");
                        }
                      }}
                      placeholder="Select or create a client manager"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={editData.data?.status || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            status: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select status</option>
                      {constantValues.statusOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {isPerson ? (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Filing Status
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.filing_status}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Filing Details
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.filing_details}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          State Filing
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatValue(client.data.state_filing)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Name
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.name}
                        </dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Name
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Contact Name
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.contact_name}
                        </dd>
                      </div>
                      {helperService.shouldRender("dba", client?.data?.dba) && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            DBA
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {client.data.dba}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Filing Status
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.filing_status}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Filing State
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatValue(client.data.filing_state)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          EIN
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatEIN(client.data.ein)}
                        </dd>
                      </div>
                    </>
                  )}

                  {/* Common fields */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.email}
                    </dd>
                  </div>
                  {helperService.shouldRender(
                    "secondary_email",
                    client?.data?.secondary_email,
                  ) && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Secondary Email
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {client.data.secondary_email}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Phone Number
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.phone}
                    </dd>
                  </div>
                  {helperService.shouldRender(
                    "additional_phones",
                    client?.data?.additional_phones,
                  ) && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Additional Phone Numbers
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {client.data.additional_phones}
                      </dd>
                    </div>
                  )}

                  {isPerson ? (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          SSN
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatSSN(client.data.ssn)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Date of Birth
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatDataDate(client.data.date_birth)}
                        </dd>
                      </div>
                      {helperService.shouldRender(
                        "spouse_name",
                        client?.data?.spouse_name,
                      ) && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Spouse Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {client.data.spouse_name}
                          </dd>
                        </div>
                      )}
                      {helperService.shouldRender(
                        "spouse_ssn",
                        client?.data?.spouse_ssn,
                      ) && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Spouse SSN
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {helperService.formatSSN(client.data.spouse_ssn)}
                          </dd>
                        </div>
                      )}
                      {helperService.shouldRender(
                        "spouse_date_birth",
                        client?.data?.spouse_date_birth,
                      ) && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">
                            Spouse Date of Birth
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {helperService.formatDataDate(
                              client.data.spouse_date_birth,
                            )}
                          </dd>
                        </div>
                      )}
                    </>
                  ) : (
                    <></>
                  )}

                  {/* Common fields */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Address
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.address}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      City, State, Zip
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.city}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Country
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.country}
                    </dd>
                  </div>

                  {isPerson ? (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Foreign
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.international}
                        </dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Incorporation Date
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {helperService.formatDataDate(
                            client.data.incorporation_date,
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Incorporation State
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.incorporation_state}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Industry
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.industry}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Foreign
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.international}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Accounting Method
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {client.data.accounting_method}
                        </dd>
                      </div>
                    </>
                  )}

                  {/* Common fields */}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Preferred Method of Contact
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.contact_method}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Language
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.language}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Sales Representative
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.sales_rep_name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Client Manager
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.client_manager_name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Status
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {client.data.status}
                    </dd>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* QuickBooks Tab */}
          {activeTab === "quickbooks" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  QuickBooks Information
                </h3>
                <div className="flex items-center gap-2">
                  {client?.quickbooks ? (
                    isQuickbooksEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={cancelQuickbooksEdit}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveQuickbooks}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                        >
                          Save QuickBooks
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={startQuickbooksEdit}
                          className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteQuickbooks}
                          className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )
                  ) : isQuickbooksEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={cancelQuickbooksEdit}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveQuickbooks}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                      >
                        Save QuickBooks
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={startQuickbooksEdit}
                      className="flex items-center px-4 py-2 border border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add QuickBooks
                    </button>
                  )}
                </div>
              </div>

              {isQuickbooksEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={quickbooksDraft.type}
                      onChange={(e) =>
                        setQuickbooksDraft((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select type</option>
                      {constantValues.quickbooksTypeOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={quickbooksDraft.username}
                      onChange={(e) =>
                        setQuickbooksDraft((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={quickbooksDraft.password}
                        onChange={(e) =>
                          setQuickbooksDraft((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOffIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : client?.quickbooks ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(client.quickbooks).map(([key, value]) => {
                    if (!value) return null;
                    const normalizedKey = key.toLowerCase();
                    if (
                      constantValues.hiddenQuickbooksFields.has(normalizedKey)
                    )
                      return null;
                    const label =
                      constantValues.labelMappingQuickbooks[normalizedKey] ||
                      key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase());
                    const isPassword = normalizedKey === "password";
                    const displayValue = isPassword
                      ? showPassword
                        ? value
                        : "••••••••"
                      : value;
                    return (
                      <div key={key}>
                        <dt className="text-sm font-medium text-gray-500">
                          {label}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                          {displayValue}
                          {isPassword && (
                            <button
                              type="button"
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() => setShowPassword(!showPassword)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? (
                                <EyeOffIcon className="w-5 h-5" />
                              ) : (
                                <EyeIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {(key === "username" || isPassword) && (
                            <button
                              type="button"
                              onClick={() => handleCopyValue(value, label)}
                              className="text-gray-400 hover:text-primary-600 disabled:opacity-40"
                              disabled={!value}
                              aria-label={`Copy ${label}`}
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No QuickBooks data available
                </p>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Documents
                </h3>

                {isEditing && userPermission === "masterAccess" ? (
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <input
                      type="text"
                      value={editData.documents?.url || ""}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          documents: {
                            ...(prev.documents || {}),
                            url: e.target.value,
                          },
                        }))
                      }
                      className="w-full md:w-[550px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                ) : client.documents?.url ? (
                  <a
                    href={client.documents.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> Open SharePoint
                  </a>
                ) : (
                  <span className="text-sm text-gray-500 italic">
                    No SharePoint link available
                  </span>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-1">
                  SharePoint Integration
                </h4>
                <p className="text-gray-600">
                  All client documents are managed through SharePoint.{" "}
                  {client.documents?.url
                    ? "Use the button above to access the folder."
                    : "Add the SharePoint URL above to enable quick access."}
                </p>
              </div>
            </div>
          )}

          {/* Credentials Tab */}
          {activeTab === "credentials" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Credentials & Access
                </h3>
                <button
                  type="button"
                  onClick={() => startEditCredential()}
                  disabled={Boolean(editingCredentialId)}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    editingCredentialId
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-dashed border-gray-300 text-gray-700 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Credential
                </button>
              </div>

              {editingCredentialId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {credentialDraft.id ? "Edit Credential" : "New Credential"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entity Name
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.entity_name}
                        onChange={(e) =>
                          setCredentialDraft((prev) => ({
                            ...prev,
                            entity_name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entity Link
                      </label>
                      <input
                        type="url"
                        value={credentialDraft.entity_link}
                        onChange={(e) =>
                          setCredentialDraft((prev) => ({
                            ...prev,
                            entity_link: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.username}
                        onChange={(e) =>
                          setCredentialDraft((prev) => ({
                            ...prev,
                            username: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="text"
                        value={credentialDraft.password}
                        onChange={(e) =>
                          setCredentialDraft((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelCredentialEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveCredential}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Save Credential
                    </button>
                  </div>
                </div>
              )}

              {Array.isArray(client.credentials) &&
              client.credentials.length > 0 ? (
                <div className="space-y-4">
                  {[...client.credentials]
                    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
                    .map((cred) => {
                      if (editingCredentialId === cred.id) return null;
                      const label =
                        cred.entity_name?.trim() || "Unnamed entity";
                      return (
                        <div
                          key={cred.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 space-y-4"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                {label}
                              </h4>
                              {cred.entity_link ? (
                                <a
                                  href={cred.entity_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-primary-600 hover:underline break-words"
                                >
                                  {cred.entity_link}
                                </a>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No link provided
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => startEditCredential(cred)}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCredential(cred)}
                                className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Username
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                                {cred.username ? (
                                  cred.username
                                ) : (
                                  <span className="text-gray-400 italic">
                                    Not provided
                                  </span>
                                )}
                                {cred.username && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleCopyValue(
                                        cred.username,
                                        `${label} username`,
                                      )
                                    }
                                    className="text-gray-400 hover:text-primary-600"
                                    aria-label={`Copy username for ${label}`}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Password
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                                {cred.password ? (
                                  visibleCredentialPasswords.has(cred.id) ? (
                                    cred.password
                                  ) : (
                                    "••••••••"
                                  )
                                ) : (
                                  <span className="text-gray-400 italic">
                                    Not provided
                                  </span>
                                )}
                                {cred.password && (
                                  <>
                                    <button
                                      type="button"
                                      aria-label={
                                        visibleCredentialPasswords.has(cred.id)
                                          ? "Hide password"
                                          : "Show password"
                                      }
                                      onClick={() =>
                                        toggleCredentialPasswordVisibility(
                                          cred.id,
                                        )
                                      }
                                      className="text-gray-400 hover:text-gray-700"
                                    >
                                      {visibleCredentialPasswords.has(
                                        cred.id,
                                      ) ? (
                                        <EyeOffIcon className="h-4 w-4" />
                                      ) : (
                                        <EyeIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleCopyValue(
                                          cred.password,
                                          `${label} password`,
                                        )
                                      }
                                      className="text-gray-400 hover:text-primary-600"
                                      aria-label={`Copy password for ${label}`}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-gray-500 italic">No credentials stored</p>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === "notes" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
              </div>

              {/* Add Note */}
              <form
                onSubmit={handleAddNote}
                className="bg-gray-50 rounded-lg p-4"
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Note
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newNoteTitle}
                    onChange={(e) => setNewNoteTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex gap-3">
                    <textarea
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      placeholder="Write your note…"
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => noteFileInputRef.current?.click()}
                        className="flex items-center justify-center px-4 py-2 border border-dashed border-gray-300 text-gray-600 hover:text-primary-600 hover:border-primary-500 rounded-lg transition-colors"
                      >
                        <Paperclip className="h-4 w-4 mr-2" /> Upload Attachment
                      </button>
                      <button
                        type="submit"
                        className="flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add
                      </button>
                    </div>
                  </div>
                  <input
                    ref={noteFileInputRef}
                    type="file"
                    onChange={handleNoteFileChange}
                    className="hidden"
                  />
                  {newNoteFile && (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                        <Paperclip className="h-4 w-4" />
                        <span className="break-all">{newNoteFile.name}</span>
                        {typeof newNoteFile.size === "number" && (
                          <span className="text-gray-400">
                            • {helperService.formatBytes(newNoteFile.size)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            handlePreviewSelectedFile(
                              newNoteFile,
                              "new-note-file",
                              newNoteFile.name,
                            )
                          }
                          className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 transition"
                        >
                          {previewLoadingKey === "new-note-file"
                            ? "Loading..."
                            : "Preview"}
                        </button>
                        <button
                          type="button"
                          onClick={clearNewNoteFile}
                          className="text-xs text-red-500 hover:text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                      {activeNotePreview?.key === "new-note-file" && (
                        <NotePreviewPanel
                          preview={activeNotePreview}
                          onClose={closeNotePreview}
                        />
                      )}
                    </div>
                  )}
                </div>
              </form>

              {/* Notes List */}
              <div className="space-y-4">
                {notes.length > 0 ? (
                  notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500">
                            <span className="font-medium text-gray-900">
                              {note.author || "You"}
                            </span>
                            {" • "}
                            <span className="text-xs text-gray-500">
                              Last updated by{" "}
                              <span className="font-medium">
                                {note.who_updated_last || note.author}
                              </span>
                            </span>
                          </span>
                          {note._optimistic && (
                            <span className="text-xs text-amber-600 mt-0.5">
                              Saving…
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {editingNoteId === note.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEditNote(note.id)}
                                className="px-2 py-1 text-xs rounded bg-primary-600 text-white hover:bg-primary-700"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditNote}
                                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => startEditNote(note)}
                                className="text-gray-400 hover:text-gray-700"
                                aria-label="Edit note"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M3 21h3.75L20.5 7.25a1.75 1.75 0 00-2.475-2.475L4.275 18.525 3 21z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={(event) =>
                                  handleDeleteNote(event, note.id)
                                }
                                className="text-gray-400 hover:text-rose-600"
                                aria-label="Delete note"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            placeholder="Title"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <textarea
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      ) : (
                        <>
                          {note.title && (
                            <h4 className="text-base font-semibold text-gray-900">
                              {note.title}
                            </h4>
                          )}
                          {note.content && (
                            <p className="text-gray-700 whitespace-pre-wrap mt-1">
                              {note.content}
                            </p>
                          )}
                          {note.asset_url && (
                            <div className="mt-3 space-y-2">
                              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                                <Paperclip className="h-4 w-4 text-gray-500" />
                                <span className="break-all">
                                  {note.asset_url.split("/").pop() ||
                                    note.asset_url}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handlePreviewNote(
                                      note,
                                      `note-${note.id}`,
                                      note.asset_url.split("/").pop() ||
                                        note.asset_url,
                                    )
                                  }
                                  className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 transition"
                                >
                                  {previewLoadingKey === `note-${note.id}`
                                    ? "Loading..."
                                    : "Preview"}
                                </button>
                                <a
                                  href={clientService.getNoteFileUrl(
                                    clientIdentifier,
                                    note.asset_url,
                                  )}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-primary-600 hover:underline"
                                >
                                  Open
                                </a>
                              </div>
                              {activeNotePreview?.key === `note-${note.id}` && (
                                <NotePreviewPanel
                                  preview={activeNotePreview}
                                  onClose={closeNotePreview}
                                />
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">
                      No notes yet. Add your first note above.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tracking Tab */}
          {activeTab === "tracking" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Time Tracking
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Timer className="h-4 w-4" />
                    <span>Total Logged:</span>
                    <span className="font-semibold text-gray-900">
                      {helperService.formatDuration(totalMs)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Start / Stop Form */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  isRunning ? handleStopTimer() : handleStartTimer(e);
                }}
                className="bg-gray-50 rounded-lg p-4"
              >
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {isRunning ? "Tracking..." : "Start a Time Entry"}
                </label>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <select
                      disabled={isRunning}
                      value={selectedMember}
                      onChange={(e) => setSelectedMember(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select team member</option>
                      {constantValues.teamMembers.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      disabled={isRunning}
                      value={activityType}
                      onChange={(e) => setActivityType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select activity type</option>
                      {constantValues.activityTrackingOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="text"
                      disabled={isRunning && !!startedAt}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What are you working on?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 text-sm text-gray-600">
                    {isRunning ? (
                      <span>
                        Started:{" "}
                        <span className="font-medium text-gray-900">
                          {helperService.formatTime(startedAt)}
                        </span>{" "}
                        • Elapsed:{" "}
                        <span className="font-semibold text-gray-900">
                          {helperService.formatDuration(elapsedMs)}
                        </span>
                      </span>
                    ) : (
                      <span className="text-gray-500">Not tracking</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className={`flex items-center px-4 py-2 rounded-lg text-white transition-colors ${
                      isRunning
                        ? "bg-rose-600 hover:bg-rose-700"
                        : "bg-primary-600 hover:bg-primary-700"
                    }`}
                  >
                    {isRunning ? (
                      <>
                        <Square className="h-4 w-4 mr-2" /> Stop
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" /> Start
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Sessions List */}
              <div className="space-y-4">
                {sessions.length > 0 ? (
                  sessions.map((entry) => (
                    <div
                      key={entry.id}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span
                            className={helperService.getChipClasses(
                              entry.activity_type,
                            )}
                          >
                            {constantValues.activityTrackingOptions.find(
                              (t) => t.value === entry.activity_type,
                            )?.label || entry.activity_type}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {entry.created_by || "You"}
                          </span>
                          <span className="text-xs text-gray-500">
                            {helperService.formatTime(entry.started_at)} →{" "}
                            {helperService.formatTime(entry.ended_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-900">
                            {helperService.formatDuration(entry.duration_ms)}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDeleteSession(entry.id)}
                            className="text-gray-400 hover:text-rose-600"
                            aria-label="Delete entry"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {entry.description && (
                        <p className="text-gray-700">{entry.description}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Timer className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">
                      No time logged yet. Start your first time entry above.
                    </p>
                  </div>
                )}
              </div>

              {/* Grand Total Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Total time across all logs
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {helperService.formatDuration(totalMs)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dependents Tab */}
          {activeTab === "dependants" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Dependents Information
                </h3>
                <button
                  type="button"
                  onClick={() => startEditDependant()}
                  disabled={Boolean(editingDependantId)}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    editingDependantId
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-dashed border-gray-300 text-gray-700 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dependent
                </button>
              </div>

              {editingDependantId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {dependantDraft.id ? "Edit Dependent" : "New Dependent"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={dependantDraft.name}
                        onChange={(e) =>
                          setDependantDraft((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={dependantDraft.date_birth}
                        onChange={(e) =>
                          setDependantDraft((prev) => ({
                            ...prev,
                            date_birth: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SSN
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={9}
                        value={dependantDraft.ssn}
                        onChange={(e) =>
                          setDependantDraft((prev) => ({
                            ...prev,
                            ssn: e.target.value.replace(/\D/g, "").slice(0, 9),
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relationship
                      </label>
                      <select
                        value={dependantDraft.relationship}
                        onChange={(e) =>
                          setDependantDraft((prev) => ({
                            ...prev,
                            relationship: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select relationship</option>
                        {constantValues.dependantRelationshipOptions.map(
                          (t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelDependantEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDependant}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Save Dependent
                    </button>
                  </div>
                </div>
              )}

              {client.dependants && client.dependants.length > 0 ? (
                <div className="space-y-4">
                  {client.dependants.map((dep) => {
                    if (editingDependantId === dep.id) return null;
                    return (
                      <div
                        key={dep.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div className="text-sm font-medium text-gray-900">
                            {dep.name}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => startEditDependant(dep)}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDependant(dep)}
                              className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                          <div>
                            <dt className="text-gray-500">Date of Birth</dt>
                            <dd className="text-gray-900">
                              {dep.date_birth
                                ? helperService.formatDataDate(dep.date_birth)
                                : "—"}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">SSN</dt>
                            <dd className="text-gray-900">
                              {dep.ssn ? helperService.formatSSN(dep.ssn) : "—"}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Relationship</dt>
                            <dd className="text-gray-900">
                              {dep.relationship || "—"}
                            </dd>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No dependents information available
                </p>
              )}
            </div>
          )}

          {/* Ownership Tab */}
          {activeTab === "ownership" && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ownership Information
                </h3>
                <button
                  type="button"
                  onClick={() => startEditOwner()}
                  disabled={Boolean(editingOwnerId)}
                  className={`flex items-center px-4 py-2 rounded-lg border ${
                    editingOwnerId
                      ? "border-gray-200 text-gray-400 cursor-not-allowed"
                      : "border-dashed border-gray-300 text-gray-700 hover:bg-gray-50"
                  } transition-colors`}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Owner
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  Total ownership:{" "}
                  <span
                    className={
                      ownershipOver
                        ? "text-red-600 font-semibold"
                        : "text-gray-900 font-semibold"
                    }
                  >
                    {ownershipTotal.toFixed(2)}%
                  </span>
                </div>
                {ownershipOver && (
                  <div className="text-sm text-red-600">
                    Total exceeds 100%. Please adjust.
                  </div>
                )}
              </div>

              {editingOwnerId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {ownerDraft.id ? "Edit Owner" : "New Owner"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={ownerDraft.name}
                        onChange={(e) =>
                          setOwnerDraft((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SSN / EIN
                      </label>
                      <input
                        type="text"
                        maxLength={9}
                        value={ownerDraft.ssn}
                        onChange={(e) =>
                          setOwnerDraft((prev) => ({
                            ...prev,
                            ssn: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={ownerDraft.date_birth}
                        onChange={(e) =>
                          setOwnerDraft((prev) => ({
                            ...prev,
                            date_birth: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Percentage
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={ownerDraft.percentage}
                          onChange={(e) =>
                            setOwnerDraft((prev) => ({
                              ...prev,
                              percentage: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        <span className="absolute inset-y-0 right-3 flex items-center text-gray-400">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        value={ownerDraft.address}
                        onChange={(e) =>
                          setOwnerDraft((prev) => ({
                            ...prev,
                            address: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelOwnerEdit}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveOwner}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                    >
                      Save Owner
                    </button>
                  </div>
                </div>
              )}

              {client.ownership && client.ownership.length > 0 ? (
                <div className="space-y-4">
                  {client.ownership.map((own) => {
                    if (editingOwnerId === own.id) return null;
                    return (
                      <div
                        key={own.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between gap-2 mb-4">
                          <div className="text-sm font-medium text-gray-900">
                            {own.name || "Unnamed"}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => startEditOwner(own)}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteOwner(own)}
                              className="px-3 py-1 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {own.ssn && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                SSN / EIN
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {own.ssn}
                              </dd>
                            </div>
                          )}
                          {own.date_birth && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Date of Birth
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {helperService.formatDataDate(own.date_birth)}
                              </dd>
                            </div>
                          )}
                          {own.percentage !== undefined &&
                            own.percentage !== "" && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Percentage
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {helperService.formatPercentDisplay(
                                    own.percentage,
                                  )}
                                </dd>
                              </div>
                            )}
                          {own.address && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Address
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {own.address}
                              </dd>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No ownership information available
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
